import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TourProvider } from '../contexts/TourContext';
import { SYSTEM_VERSION } from '../version';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  BottomNavigation,
  BottomNavigationAction,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Business,
  Notifications,
  AccountCircle,
  Logout,
  PlaylistAddCheck,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import DynamicSidebar from './DynamicSidebar';
import FiscalYearSwitcher from './FiscalYearSwitcher';
import TourButton from './Tour/TourButton';

const drawerWidth = 240;
const collapsedDrawerWidth = 72;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAuth();

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000); // هر 30 ثانیه
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/alerts/unread/count');
      setUnreadCount(response.data);
    } catch (error) {
      // Silent fail
    }
  };


  // برای موبایل فقط ۴ آیتم اصلی در نوار پایین (برای دسترسی بهتر)
  const mobileNavItems = [
    { text: 'داشبورد', icon: <Dashboard />, path: '/dashboard' },
    { text: 'کارتابل', icon: <PlaylistAddCheck />, path: '/my-tasks' },
    { text: 'پروژه‌ها', icon: <Business />, path: '/projects' },
    {
      text: 'اعلان‌ها',
      icon: (
        <Badge badgeContent={unreadCount} color="error">
          <Notifications />
        </Badge>
      ),
      path: '/alerts',
    },
  ];

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setDrawerOpen(!drawerOpen);
    }
  };

  const handleMobileMenuClose = () => {
    setMobileOpen(false);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };


  return (
    <TourProvider>
      <Box sx={{ display: 'flex', position: 'relative', minHeight: '100vh' }}>
        <AppBar
        position="fixed"
        sx={{
          width: '100%',
          boxShadow: 3,
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'row-reverse' }}>
          <IconButton onClick={handleMenuClick} size="small" sx={{ ml: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'rgba(255,255,255,0.2)' }}>
              {user?.fullName?.charAt(0) || <AccountCircle />}
            </Avatar>
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
            سیستم جامع مالی و اداری
          </Typography>
          <Typography variant="caption" sx={{ ml: 2, bgcolor: 'rgba(255,255,255,0.15)', px: 1.5, py: 0.5, borderRadius: 1, fontSize: '0.75rem' }}>
            v{SYSTEM_VERSION}
          </Typography>
          <FiscalYearSwitcher />
          <TourButton />
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ ml: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            sx={{ mt: 1 }}
            PaperProps={{
              sx: {
                boxShadow: 3,
                minWidth: 180,
              }
            }}
          >
            <MenuItem onClick={() => { navigate('/profile'); handleMenuClose(); }} sx={{ gap: 1 }}>
              <AccountCircle /> پروفایل
            </MenuItem>
            <MenuItem onClick={handleLogout} sx={{ gap: 1 }}>
              <Logout /> خروج
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {!isMobile && (
        <DynamicSidebar
          open={drawerOpen}
          drawerWidth={drawerWidth}
          collapsedWidth={collapsedDrawerWidth}
          anchor="right"
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
        />
      )}

      {isMobile && (
        <DynamicSidebar
          open={mobileOpen}
          drawerWidth={drawerWidth}
          collapsedWidth={collapsedDrawerWidth}
          anchor="right"
          isMobile={true}
          onMobileClose={handleMobileMenuClose}
          onOpen={() => setMobileOpen(true)}
          onClose={handleMobileMenuClose}
        />
      )}

      <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3, md: 4 },
            width: '100%',
            mt: { xs: 7, md: 8 },
            pb: { xs: 'calc(72px + env(safe-area-inset-bottom, 0px))', md: 4 },
            boxSizing: 'border-box',
            overflowX: 'hidden',
            bgcolor: '#f5f5f5',
            // ارتفاع باید منهای margin-top باشد وگرنه سند همیشه ۶۴px سرریز می‌کند و اسکرول بی‌مورد می‌آید
            minHeight: { xs: 'calc(100vh - 56px)', md: 'calc(100vh - 64px)' },
          }}
        >
          {children}
        </Box>

        {isMobile && (
          <BottomNavigation
          value={location.pathname}
          onChange={(_, newValue) => navigate(newValue)}
          showLabels
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            width: '100%',
            maxWidth: '100vw',
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
            paddingLeft: 'env(safe-area-inset-left, 0px)',
            paddingRight: 'env(safe-area-inset-right, 0px)',
            minHeight: 56,
            boxSizing: 'border-box',
            '& .MuiBottomNavigationAction-root': {
              minWidth: 0,
              flex: 1,
              padding: '6px 4px',
              minHeight: 48,
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.65rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
        >
          {mobileNavItems.map((item) => (
            <BottomNavigationAction
              key={item.text}
              label={item.text}
              icon={item.icon}
              value={item.path}
            />
          ))}
        </BottomNavigation>
      )}
    </Box>
  </TourProvider>
  );

};

export default Layout;
